import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { ProfessionServicesService } from '../services/profession-services.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RecruitmentApiService } from '../services/recruitment-api.service';
import { AuthService } from '../services/auth.service';

interface BehaviorOption {
  categoryId: string;
  categoryName: string;
  subcategoryId: string;
  categoryDescription?: string;
}

interface BehavioralScoring {
  'overall-cutoff': number;
  evaluate: string;
  'personality-contribution': {
    [subcategoryId: string]: {
      [categoryId: string]: number;
    };
  };
  'crossmapping-weights': {
    [crossKey: string]: number;
  };
  weights: {
    [subcategoryId: string]: number;
  };
}

@Component({
  selector: 'app-profession-score-manager',
  templateUrl: './profession-score-manager.component.html',
  styleUrls: ['./profession-score-manager.component.css'],
  standalone: false
})
export class ProfessionScoreManagerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data
  allProfessions: any[] = [];
  allSubCategories: any[] = [];
  allBehaviorOptions: BehaviorOption[] = [];
  industries: any[] = [];
  filteredProfessions: any[] = [];
  
  // UI State
  selectedIndustry: string = '';
  selectedProfession: any | null = null;
  isLoading: boolean = false;
  isSaving: boolean = false;
  searchControl = new FormControl('');
  filteredSubCategories: any[] = [];
  showSubCategoryDropdown: boolean = false;
  
  // Behavioral Scoring State
  showBehavioralScoring: boolean = false;
  isFlipping: boolean = false;
  searchBehaviorControl = new FormControl('');
  filteredBehaviorOptions: BehaviorOption[] = [];
  showBehaviorOptionDropdown: boolean = false;
  selectedCrossSubcategory1: string = '';
  selectedCrossSubcategory2: string = '';
  showCrossWeightDialog: boolean = false;
  crossWeightValue: number = 0;
  
  // New Role Type
  showNewRoleTypeDialog: boolean = false;
  newRoleTypeName: string = '';

  // Edit state
  editedProfession: any | null = null;

  // delete managers
  showNewIndustryDialog: boolean = false;
  newIndustryName: string = '';
  showDeleteIndustryDialog: boolean = false;
  industryToDelete: string = '';
  showDeleteRoleDialog: boolean = false;
  roleToDelete: any | null = null;

  showEmptyUpdateConfirmDialog: boolean = false;
  wipeWarningFields: string[] = [];
  private pendingUpdatePayload: any = null;
  isPrivateBenchmarkMode = false;
  privateOrganizationId = '';
  privateOrganizationName = '';
  editingPrivateBenchmarkId: string | null = null;
  privateBenchmarkPersisted = false;

  constructor(
    private professionService: ProfessionServicesService,
    private recruitmentApi: RecruitmentApiService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.isPrivateBenchmarkMode = this.route.snapshot.queryParamMap.get('mode') === 'private';
    this.privateOrganizationId = this.route.snapshot.queryParamMap.get('orgId') || '';
    this.editingPrivateBenchmarkId = this.route.snapshot.queryParamMap.get('privateBenchmarkId');

    if (this.isPrivateBenchmarkMode) {
      if (this.authService.getCurrentUser()?.role !== 'admin' || !this.privateOrganizationId) {
        this.router.navigate(['/admin-dashboard']);
        return;
      }
      this.recruitmentApi.setOrganizationContext(this.privateOrganizationId);
      this.loadPrivateOrganizationDetails();
    }
    this.loadInitialData();
    this.setupSearch();
    this.setupBehaviorSearch();
  }

  private loadPrivateOrganizationDetails(): void {
    this.recruitmentApi.getOrganizationDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (organization) => {
          this.privateOrganizationName = organization.name;
        },
        error: (error) => {
          console.error('Unable to load private benchmark organization', error);
          this.privateOrganizationName = 'Organization';
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.isPrivateBenchmarkMode) {
      this.recruitmentApi.setOrganizationContext(null);
    }
  }

  /**
   * Load all initial data
   */
  private async loadInitialData(): Promise<void> {
    this.isLoading = true;

    try {
      // Load professions
      const professions: any = await firstValueFrom(
        this.professionService.getProfessions().pipe(takeUntil(this.destroy$))
      );

      if (professions.success) {
        this.allProfessions = professions["allBenchmarks"];
        this.professionService.updateLocalProfessions(professions["allBenchmarks"]);
        
        this.extractIndustries();
        
        if (this.industries.length > 0) {
          this.selectIndustry(this.industries[0].name);
        }
      } else {
        throw new Error('Unable to fetch professions');
      }

      // Load subcategories
      const subcategories: any = await firstValueFrom(
        this.professionService.getSubCategories().pipe(takeUntil(this.destroy$))
      );

      if (subcategories.success) {
        this.allSubCategories = subcategories["allCategories"];
        this.filteredSubCategories = subcategories["allCategories"];
      } else {
        throw new Error('Unable to fetch subcategories');
      }

      // Load behavior options
      await this.loadBehaviorOptions();

      if (this.isPrivateBenchmarkMode && this.editingPrivateBenchmarkId) {
        await this.loadPrivateBenchmarkForEditing(this.editingPrivateBenchmarkId);
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadPrivateBenchmarkForEditing(benchmarkId: string): Promise<void> {
    const benchmarks = await firstValueFrom(
      this.recruitmentApi.getBenchmarks().pipe(takeUntil(this.destroy$))
    );
    const benchmark = benchmarks.find((item) => item.benchmarkId === benchmarkId);
    if (!benchmark) {
      throw new Error('Private benchmark was not found');
    }

    const rawScore = { ...(benchmark.rawScore || {}) };
    const profession = {
      industry: benchmark.department || 'Private',
      roleType: benchmark.roleTitle,
      distribution: { ...(benchmark.distribution || {}) },
      'raw-score': rawScore,
      behavioral: benchmark.behavioral,
      description: benchmark.description || '',
      privateBenchmarkId: benchmark.benchmarkId
    };
    this.ensureIndustryVisible(profession.industry);
    this.selectIndustry(profession.industry);
    this.selectProfession(profession);
    this.editingPrivateBenchmarkId = benchmark.benchmarkId;
    this.privateBenchmarkPersisted = true;
  }

  private ensureIndustryVisible(industry: string): void {
    if (!this.industries.some((item) => item.name === industry)) {
      this.industries.push({ name: industry, roleTypes: [] });
    }
  }

  /**
   * Load behavior options
   */
  private async loadBehaviorOptions(): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.professionService.getBehaviorOptionCategories()
      );
  
      if (response?.success && response?.allBehaviorCategories) {
        this.allBehaviorOptions = response.allBehaviorCategories || [];
        this.filteredBehaviorOptions = this.allBehaviorOptions;
      } else {
        this.allBehaviorOptions = [];
        this.filteredBehaviorOptions = [];
      }
    } catch (error) {
      console.error('Error loading behavior options:', error);
      this.allBehaviorOptions = [];
      this.filteredBehaviorOptions = [];
    }
  }

  /**
   * Extract industries from professions
   */
  private extractIndustries(): void {
    const industryMap = new Map<string, Set<string>>();

    this.allProfessions.forEach(profession => {
      if (!industryMap.has(profession.industry)) {
        industryMap.set(profession.industry, new Set());
      }
      industryMap.get(profession.industry)?.add(profession.roleType);
    });

    this.industries = Array.from(industryMap.entries()).map(([name, roleTypesSet]) => ({
      name,
      roleTypes: Array.from(roleTypesSet)
    }));
  }

  /**
   * Setup search functionality for subcategories
   */
  private setupSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.filterSubCategories(searchTerm || '');
      });
  }

  /**
   * Setup search functionality for behavior options
   */
  private setupBehaviorSearch(): void {
    this.searchBehaviorControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.filterBehaviorOptions(searchTerm || '');
      });
  }

  /**
   * Filter subcategories based on search term
   */
  private filterSubCategories(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.filteredSubCategories = this.allSubCategories;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredSubCategories = this.allSubCategories.filter(sub =>
      sub.subTypeId.toLowerCase().includes(term) ||
      sub.typeId.toLowerCase().includes(term) ||
      (sub.subType && sub.subType.toLowerCase().includes(term))
    );
  }

  /**
   * Filter behavior options based on search term
   */
  private filterBehaviorOptions(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.filteredBehaviorOptions = this.allBehaviorOptions;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredBehaviorOptions = this.allBehaviorOptions.filter(opt =>
      opt.categoryName.toLowerCase().includes(term) ||
      opt.subcategoryId.toLowerCase().includes(term) ||
      (opt.categoryDescription && opt.categoryDescription.toLowerCase().includes(term))
    );
  }

  /**
   * Select an industry and filter professions
   */
  selectIndustry(industry: string): void {
    this.selectedIndustry = industry;
    this.filteredProfessions = this.professionService.getProfessionsByIndustry(
      this.allProfessions,
      industry
    );
    this.selectedProfession = null;
    this.editedProfession = null;
    this.showBehavioralScoring = false;
  }

  /**
   * Select a profession to view/edit
   */
  selectProfession(profession: any): void {
    if (this.isPrivateBenchmarkMode) {
      this.editingPrivateBenchmarkId = null;
      this.privateBenchmarkPersisted = false;
    }
    this.selectedProfession = profession;
    this.editedProfession = JSON.parse(JSON.stringify(profession));

    // Initialize behavioral structure only if it doesn't exist (lazy initialization)
    if (!this.editedProfession.behavioral) {
      this.editedProfession.behavioral = {
        'overall-cutoff': 85,
        'evaluate': 'False',
        'personality-contribution': {},
        'crossmapping-weights': {},
        'weights': {}
      };
    } else if (this.editedProfession.behavioral.evaluate !== 'True' &&
               this.editedProfession.behavioral.evaluate !== 'False') {
      // Existing behavioral values are enabled by default unless a user has
      // explicitly disabled them.
      this.editedProfession.behavioral.evaluate =
        this.hasBehavioralValues(this.editedProfession.behavioral) ? 'True' : 'False';
    }

    this.showBehavioralScoring =
      this.editedProfession.behavioral.evaluate === 'True';
  }

  /**
   * Toggle between cognitive and behavioral scoring
   */
  toggleBehavioralScoring(): void {
  if (this.isFlipping) return;
 
  this.isFlipping = true;
  this.showBehavioralScoring = !this.showBehavioralScoring;
 
  setTimeout(() => {
    this.isFlipping = false;
  }, 600);
}

setBehavioralEnabled(enabled: boolean): void {
  if (!this.editedProfession?.behavioral) return;
  this.editedProfession.behavioral.evaluate = enabled ? 'True' : 'False';
}

private hasBehavioralValues(behavioral: any): boolean {
  const contributions = behavioral?.['personality-contribution'] || {};

  return Object.values(contributions).some(
    (categoryScores: any) =>
      categoryScores && Object.keys(categoryScores).length > 0
  );
}

  /**
   * Get subcategory keys from a profession
   */
  getSubCategoryKeys(profession: any): string[] {
    const distributionKeys = Object.keys(profession.distribution || {});
    const rawScoreKeys = Object.keys(profession['raw-score'] || {});
    
    return Array.from(new Set([...distributionKeys, ...rawScoreKeys]));
  }

  /**
   * Get unique subcategory IDs from behavioral scoring
   */
  getSubcategoryIds(): string[] {
    if (!this.editedProfession?.behavioral?.['personality-contribution']) {
      return [];
    }
    return Object.keys(this.editedProfession.behavioral['personality-contribution']);
  }

  /**
   * Get behavior options for a subcategory
   */
  getBehaviorOptionsForSubcategory(subcategoryId: string): BehaviorOption[] {
    if (!this.editedProfession?.behavioral?.['personality-contribution']?.[subcategoryId]) {
      return [];
    }
    
    const categoryIds = Object.keys(this.editedProfession.behavioral['personality-contribution'][subcategoryId]);
    return this.allBehaviorOptions.filter(opt => categoryIds.includes(opt.categoryId));
  }

  /**
   * Get subcategory display name
   */
  getSubcategoryDisplayName(subcategoryId: string): string {
    return subcategoryId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Add a subcategory to the edited profession
   */
  addSubCategory(subCategory: any): void {
    if (!this.editedProfession) return;

    const key = subCategory.subTypeId;

    if (!this.editedProfession.distribution[key]) {
      this.editedProfession.distribution[key] = 10;
    }

    if (!this.editedProfession['raw-score'][key]) {
      this.editedProfession['raw-score'][key] = 0;
    }

    this.showSubCategoryDropdown = false;
    this.searchControl.setValue('');
  }

  /**
   * Add a behavior option
   */
  addBehaviorOption(option: BehaviorOption): void {
    if (!this.editedProfession?.behavioral) return;

    const hadBehavioralValues = this.hasBehavioralValues(this.editedProfession.behavioral);
    const subcategoryId = option.subcategoryId;
    const categoryId = option.categoryId;

    // Initialize subcategory in personality-contribution if doesn't exist
    if (!this.editedProfession.behavioral['personality-contribution'][subcategoryId]) {
      this.editedProfession.behavioral['personality-contribution'][subcategoryId] = {};
    }

    // Add the option with default score
    if (!this.editedProfession.behavioral['personality-contribution'][subcategoryId][categoryId]) {
      this.editedProfession.behavioral['personality-contribution'][subcategoryId][categoryId] = 50;
    }

    // Initialize weight for subcategory if doesn't exist
    if (!this.editedProfession.behavioral.weights[subcategoryId]) {
      this.editedProfession.behavioral.weights[subcategoryId] = 0.5;
    }

    // Enable evaluation when the first behavioral value is configured.
    if (!hadBehavioralValues) {
      this.editedProfession.behavioral.evaluate = 'True';
    }

    this.showBehaviorOptionDropdown = false;
    this.searchBehaviorControl.setValue('');
  }

  /**
   * Add all behavior options
   */
  addAllBehaviorOptions(): void {
    if (!this.editedProfession?.behavioral) return;

    const hadBehavioralValues = this.hasBehavioralValues(this.editedProfession.behavioral);

    this.allBehaviorOptions.forEach(option => {
      const subcategoryId = option.subcategoryId;
      const categoryId = option.categoryId;

      if (!this.editedProfession.behavioral['personality-contribution'][subcategoryId]) {
        this.editedProfession.behavioral['personality-contribution'][subcategoryId] = {};
      }

      if (!this.editedProfession.behavioral['personality-contribution'][subcategoryId][categoryId]) {
        this.editedProfession.behavioral['personality-contribution'][subcategoryId][categoryId] = 50;
      }

      if (!this.editedProfession.behavioral.weights[subcategoryId]) {
        this.editedProfession.behavioral.weights[subcategoryId] = 0.5;
      }
    });

    if (!hadBehavioralValues &&
        this.hasBehavioralValues(this.editedProfession.behavioral)) {
      this.editedProfession.behavioral.evaluate = 'True';
    }

    this.showBehaviorOptionDropdown = false;
  }

  /**
   * Remove subcategory
   */
  removeSubCategory(key: string): void {
    if (!this.editedProfession) return;

    delete this.editedProfession.distribution[key];
    delete this.editedProfession['raw-score'][key];
  }

  /**
   * Remove behavior option
   */
  removeBehaviorOption(subcategoryId: string, categoryId: string): void {
    if (!this.editedProfession?.behavioral) return;

    delete this.editedProfession.behavioral['personality-contribution'][subcategoryId][categoryId];

    // If no more options in this subcategory, remove the subcategory and its weight
    if (Object.keys(this.editedProfession.behavioral['personality-contribution'][subcategoryId]).length === 0) {
      delete this.editedProfession.behavioral['personality-contribution'][subcategoryId];
      delete this.editedProfession.behavioral.weights[subcategoryId];

      // Remove any cross mappings involving this subcategory
      const crossKeys = Object.keys(this.editedProfession.behavioral['crossmapping-weights']);
      crossKeys.forEach(key => {
        if (key.includes(subcategoryId)) {
          delete this.editedProfession.behavioral['crossmapping-weights'][key];
        }
      });
    }

    if (!this.hasBehavioralValues(this.editedProfession.behavioral)) {
      this.editedProfession.behavioral.evaluate = 'False';
    }
  }

  /**
   * Remove entire subcategory group
   */
  removeSubcategoryGroup(subcategoryId: string): void {
    if (!this.editedProfession?.behavioral) return;

    delete this.editedProfession.behavioral['personality-contribution'][subcategoryId];
    delete this.editedProfession.behavioral.weights[subcategoryId];

    // Remove any cross mappings involving this subcategory
    const crossKeys = Object.keys(this.editedProfession.behavioral['crossmapping-weights']);
    crossKeys.forEach(key => {
      if (key.includes(subcategoryId)) {
        delete this.editedProfession.behavioral['crossmapping-weights'][key];
      }
    });

    if (!this.hasBehavioralValues(this.editedProfession.behavioral)) {
      this.editedProfession.behavioral.evaluate = 'False';
    }
  }

  /**
   * Update distribution value
   */
  updateDistributionValue(key: string, value: string): void {
    if (!this.editedProfession) return;

    const numValue = parseFloat(value);
    this.editedProfession.distribution[key] = isNaN(numValue) ? value : numValue;
  }

  /**
   * Update raw score value
   */
  updateRawScoreValue(key: string, value: string): void {
    if (!this.editedProfession) return;

    const numValue = parseFloat(value);
    this.editedProfession['raw-score'][key] = isNaN(numValue) ? value : numValue;
  }

  /**
   * Update behavior option score
   */
  updateBehaviorOptionScore(subcategoryId: string, categoryId: string, value: string): void {
    if (!this.editedProfession?.behavioral) return;

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      this.editedProfession.behavioral['personality-contribution'][subcategoryId][categoryId] = numValue;
    }
  }

  /**
   * Update subcategory weight
   */
  updateSubcategoryWeight(subcategoryId: string, value: string): void {
    if (!this.editedProfession?.behavioral) return;

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      this.editedProfession.behavioral.weights[subcategoryId] = numValue;
    }
  }

  /**
   * Update overall cutoff
   */
  updateOverallCutoff(value: string): void {
    if (!this.editedProfession?.behavioral) return;

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      this.editedProfession.behavioral['overall-cutoff'] = numValue;
    }
  }

  /**
   * Open cross weight dialog
   */
  openCrossWeightDialog(): void {
    this.selectedCrossSubcategory1 = '';
    this.selectedCrossSubcategory2 = '';
    this.crossWeightValue = 0;
    this.showCrossWeightDialog = true;
  }

  /**
   * Add cross mapping weight
   */
  addCrossWeight(): void {
    if (!this.editedProfession?.behavioral) return;
    if (!this.selectedCrossSubcategory1 || !this.selectedCrossSubcategory2) {
      alert('Please select two different subcategories');
      return;
    }

    if (this.selectedCrossSubcategory1 === this.selectedCrossSubcategory2) {
      alert('Please select two different subcategories');
      return;
    }

    const crossKey = `${this.selectedCrossSubcategory1}#${this.selectedCrossSubcategory2}`;
    this.editedProfession.behavioral['crossmapping-weights'][crossKey] = this.crossWeightValue;

    this.showCrossWeightDialog = false;
  }

  /**
   * Get cross mappings
   */
  getCrossMappings(): string[] {
    if (!this.editedProfession?.behavioral?.['crossmapping-weights']) {
      return [];
    }
    return Object.keys(this.editedProfession.behavioral['crossmapping-weights']);
  }

  /**
   * Remove cross mapping
   */
  removeCrossMapping(crossKey: string): void {
    if (!this.editedProfession?.behavioral) return;
    delete this.editedProfession.behavioral['crossmapping-weights'][crossKey];
  }

  /**
   * Update cross mapping weight
   */
  updateCrossWeight(crossKey: string, value: string): void {
    if (!this.editedProfession?.behavioral) return;

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      this.editedProfession.behavioral['crossmapping-weights'][crossKey] = numValue;
    }
  }

  /**
   * Get behavior option details
   */
  getBehaviorOptionDetails(categoryId: string): BehaviorOption | undefined {
    return this.allBehaviorOptions.find(opt => opt.categoryId === categoryId);
  }

  /**
   * Save the edited profession
   */
  updateProfession(): void {
  if (!this.editedProfession) return;

  const behavioral = this.editedProfession.behavioral;
  if (behavioral) {
    if (!this.hasBehavioralValues(behavioral)) {
      behavioral.evaluate = 'False';
    } else if (behavioral.evaluate !== 'False') {
      behavioral.evaluate = 'True';
    }
  }
 
  const payload: any = {
    industry: this.editedProfession.industry,
    roleType: this.editedProfession.roleType,
    distribution: this.editedProfession.distribution,
    'raw-score': this.editedProfession['raw-score'],
    behavioral
  };

  if (this.isPrivateBenchmarkMode) {
    this.savePrivateBenchmark(payload);
    return;
  }
 
  this.sendUpdate(payload);
}

private savePrivateBenchmark(payload: any): void {
  const benchmarkId = this.editingPrivateBenchmarkId || this.slugify(payload.roleType);
  if (!benchmarkId || !Object.keys(payload['raw-score'] || {}).length) {
    alert('A role name and at least one raw score are required.');
    return;
  }

  this.isSaving = true;
  this.recruitmentApi.upsertPrivateBenchmark({
    benchmarkId,
    roleTitle: payload.roleType,
    department: payload.industry,
    description: this.editedProfession.description || '',
    distribution: { ...(payload.distribution || {}) },
    rawScore: { ...(payload['raw-score'] || {}) },
    topTraits: Object.keys(payload['raw-score'] || {}).slice(0, 3),
    behavioral: payload.behavioral
  }).pipe(takeUntil(this.destroy$)).subscribe({
    next: (savedBenchmark) => {
      const savedProfession = {
        ...this.editedProfession,
        privateBenchmarkId: savedBenchmark.benchmarkId
      };
      this.editingPrivateBenchmarkId = savedBenchmark.benchmarkId;
      this.privateBenchmarkPersisted = true;
      this.selectedProfession = JSON.parse(JSON.stringify(savedProfession));
      this.editedProfession = JSON.parse(JSON.stringify(savedProfession));
      this.isSaving = false;
      alert('Private benchmark saved successfully!');
    },
    error: (error) => {
      console.error('Error saving private benchmark:', error);
      this.isSaving = false;
      alert(error?.error?.error?.message || 'Error saving private benchmark. Please try again.');
    }
  });
}

canSaveProfession(): boolean {
  return !!this.editedProfession &&
    (!this.privateBenchmarkPersisted || this.hasUnsavedChanges()) &&
    !this.isSaving;
}

private slugify(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
 
private sendUpdate(payload: any): void {
  this.isSaving = true;
 
  this.professionService.updateProfession(payload)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (updatedProfessionResponse) => {
        // The update endpoint may return only the fields it changed. Build the
        // local state from the existing profession and the payload we know was
        // persisted instead of replacing it with a potentially partial response.
        const existingProfession = this.allProfessions.find(
          p => p.industry === payload.industry &&
               p.roleType === payload.roleType
        ) || this.selectedProfession || {};

        const updatedProfession = {
          ...existingProfession,
          ...(updatedProfessionResponse || {}),
          ...payload,
          distribution: { ...(payload.distribution || {}) },
          'raw-score': { ...(payload['raw-score'] || {}) }
        };

        // Do not keep the confirmation-only request flag in the profession state.
        delete updatedProfession.confirmEmptyUpdate;

        const index = this.allProfessions.findIndex(
          p => p.industry === payload.industry &&
               p.roleType === payload.roleType
        );
 
        if (index !== -1) {
          this.allProfessions[index] = updatedProfession;
        } else {
          this.allProfessions.push(updatedProfession);
        }
 
        this.selectedProfession = updatedProfession;
        this.editedProfession = JSON.parse(JSON.stringify(updatedProfession));
        this.filteredProfessions = this.professionService.getProfessionsByIndustry(
          this.allProfessions,
          this.selectedIndustry
        );
        this.professionService.updateLocalProfessions(this.allProfessions);
 
        this.isSaving = false;
        this.showEmptyUpdateConfirmDialog = false;
        this.pendingUpdatePayload = null;
        this.wipeWarningFields = [];
        alert('Profession updated successfully!');
      },
      error: (error) => {
        this.isSaving = false;
 
        // Backend refused because this write would clear previously-saved
        // data. Surface a confirm dialog instead of a generic error.
        const body = error?.error;
        if (error?.status === 409 && body?.requiresConfirmation) {
          this.wipeWarningFields = body.wouldWipeFields || [];
          this.pendingUpdatePayload = payload;
          this.showEmptyUpdateConfirmDialog = true;
          return;
        }
 
        console.error('Error updating profession:', error);
        alert('Error updating profession. Please try again.');
      }
    });
}
 
/**
 * User confirmed they want to proceed despite clearing previously-saved
 * fields (shown in the confirm dialog via wipeWarningFields).
 */
confirmEmptyUpdate(): void {
  if (!this.pendingUpdatePayload) return;
  this.sendUpdate({ ...this.pendingUpdatePayload, confirmEmptyUpdate: true });
}
 
cancelEmptyUpdate(): void {
  this.showEmptyUpdateConfirmDialog = false;
  this.pendingUpdatePayload = null;
  this.wipeWarningFields = [];
}

  /**
   * Open new role type dialog
   */
  openNewRoleTypeDialog(): void {
    this.showNewRoleTypeDialog = true;
    this.newRoleTypeName = '';
  }

  /**
   * Create a new role type
   */
createNewRoleType(): void {
  if (!this.newRoleTypeName.trim() || !this.selectedIndustry) return;

  const newProfession: any = {
    industry: this.selectedIndustry,
    roleType: this.newRoleTypeName.trim(),
    distribution: {},
    'raw-score': {}
  };

  if (this.isPrivateBenchmarkMode) {
    this.allProfessions.push(newProfession);
    this.extractIndustries();
    this.selectIndustry(newProfession.industry);
    this.selectProfession(newProfession);
    this.showNewRoleTypeDialog = false;
    this.newRoleTypeName = '';
    return;
  }

  this.isSaving = true;

  this.professionService.createRoleType(
    this.selectedIndustry,
    this.newRoleTypeName.trim(),
    newProfession
  )
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: async (createdProfession) => {
        // Re-fetch from server to get true state instead of relying on local optimistic update
        try {
          const professions: any = await firstValueFrom(
            this.professionService.getProfessions().pipe(takeUntil(this.destroy$))
          );

          if (professions.success) {
            this.allProfessions = professions["allBenchmarks"];
            this.professionService.updateLocalProfessions(professions["allBenchmarks"]);
            this.extractIndustries();
            this.selectIndustry(this.selectedIndustry);

            // Find the freshly loaded version of the created profession
            const freshProfession = this.allProfessions.find(
              p => p.industry === newProfession.industry && 
                   p.roleType === newProfession.roleType
            );
            if (freshProfession) {
              this.selectProfession(freshProfession);
            }
          }
        } catch (err) {
          console.error('Error reloading professions after create:', err);
          // Fallback to local update if refetch fails
          this.allProfessions.push(createdProfession);
          this.extractIndustries();
          this.selectIndustry(this.selectedIndustry);
          this.selectProfession(createdProfession);
        }

        this.showNewRoleTypeDialog = false;
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error creating role type:', error);
        this.isSaving = false;
        alert('Error creating role type. Please try again.');
      }
    });
}
  /**
   * Check if there are unsaved changes
   */
  hasUnsavedChanges(): boolean {
    if (!this.selectedProfession || !this.editedProfession) return false;
    
    return JSON.stringify(this.selectedProfession) !== JSON.stringify(this.editedProfession);
  }

  /**
   * Cancel editing and revert changes
   */
  cancelEditing(): void {
    if (this.selectedProfession) {
      this.editedProfession = JSON.parse(JSON.stringify(this.selectedProfession));
      this.showBehavioralScoring = !!(this.selectedProfession.behavioral && 
                                       this.selectedProfession.behavioral.evaluate === 'True');
    }
  }

  /**
   * Toggle subcategory dropdown
   */
  toggleSubCategoryDropdown(): void {
    this.showSubCategoryDropdown = !this.showSubCategoryDropdown;
    if (this.showSubCategoryDropdown) {
      this.searchControl.setValue('');
      this.filteredSubCategories = this.allSubCategories;
    }
  }

  /**
   * Toggle behavior option dropdown
   */
  toggleBehaviorOptionDropdown(): void {
    this.showBehaviorOptionDropdown = !this.showBehaviorOptionDropdown;
    if (this.showBehaviorOptionDropdown) {
      this.searchBehaviorControl.setValue('');
      this.filteredBehaviorOptions = this.allBehaviorOptions;
    }
  }

  /**
   * Get subcategory display name
   */
  getSubCategoryDisplayName(subCategoryId: string): string {
    const subCategory = this.allSubCategories.find(s => s.subTypeId === subCategoryId);
    return subCategory ? subCategory.subType || subCategoryId : subCategoryId;
  }

  /**
   * Format value for display
   */
  formatValue(value: number | string): string {
    return typeof value === 'number' ? value.toString() : value;
  }

  // delete industry managers
  openNewIndustryDialog(): void {
    this.showNewIndustryDialog = true;
    this.newIndustryName = '';
}

createNewIndustry(): void {
  if (!this.newIndustryName.trim()) return;

  const industryName = this.newIndustryName.trim();

  // Prevent duplicates
  if (this.industries.find(i => i.name.toLowerCase() === industryName.toLowerCase())) {
    alert(`Industry "${industryName}" already exists.`);
    return;
  }

  // Add optimistically so the button is visible and selectable
  this.industries.push({ name: industryName, roleTypes: [] });
  this.selectIndustry(industryName);

  this.showNewIndustryDialog = false;
  this.newIndustryName = '';

  // Immediately prompt to add a role — industries only persist via their roles
  setTimeout(() => {
    this.openNewRoleTypeDialog();
  }, 150);
}

openDeleteIndustryDialog(): void {
    if (!this.selectedIndustry) return;
    this.industryToDelete = this.selectedIndustry;
    this.showDeleteIndustryDialog = true;
}

confirmDeleteIndustry(): void {
    if (!this.industryToDelete) return;
    this.isSaving = true;

    this.professionService.deleteIndustry(this.industryToDelete)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
            next: (response) => {
                if (response.success) {
                    // Remove all professions under this industry locally
                    this.allProfessions = this.allProfessions.filter(
                        p => p.industry !== this.industryToDelete
                    );
                    this.extractIndustries();
                    this.selectedIndustry = '';
                    this.selectedProfession = null;
                    this.editedProfession = null;
                    if (this.industries.length > 0) {
                        this.selectIndustry(this.industries[0].name);
                    }
                }
                this.showDeleteIndustryDialog = false;
                this.isSaving = false;
            },
            error: (err) => {
                console.error('Error deleting industry:', err);
                this.isSaving = false;
                this.showDeleteIndustryDialog = false;
                alert('Error deleting industry. Please try again.');
            }
        });
}

openDeleteRoleDialog(profession: any): void {
    this.roleToDelete = profession;
    this.showDeleteRoleDialog = true;
}

confirmDeleteRole(): void {
    if (!this.roleToDelete) return;
    this.isSaving = true;

    this.professionService.deleteRoleType(this.roleToDelete.industry, this.roleToDelete.roleType)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
            next: (response) => {
                if (response.success) {
                    this.allProfessions = this.allProfessions.filter(
                        p => !(p.industry === this.roleToDelete.industry && 
                               p.roleType === this.roleToDelete.roleType)
                    );
                    this.extractIndustries();
                    this.selectIndustry(this.selectedIndustry);

                    if (this.selectedProfession?.roleType === this.roleToDelete.roleType) {
                        this.selectedProfession = null;
                        this.editedProfession = null;
                    }
                }
                this.roleToDelete = null;
                this.showDeleteRoleDialog = false;
                this.isSaving = false;
            },
            error: (err) => {
                console.error('Error deleting role:', err);
                this.isSaving = false;
                this.showDeleteRoleDialog = false;
                alert('Error deleting role. Please try again.');
            }
        });
}
}
